import AlertArea from "./ui/AlertArea.js";
import GeolocationDebug from "./geolocation/GeolocationDebug.js";
import global from "@/prototype/global.js";
import StateMachine from "statemachine";
import WeakIterable from "./utils/WeakIterable.js";

const log = global.log.getLogger("f_mtac.geolocation");
log.setLevel(log.levels.WARN);

const GEOLOCATION_MAXIAGE = 15000; // do not take positions older than 15000 seconds
const GEOLOCATION_TIMEOUT = 15000; // service timeout

const GEOLOCATION_ERROR_PERMISSION_DENIED = 1; // The acquisition of the geolocation information failed because the page didn't have the permission to do it.
const GEOLOCATION_ERROR_POSITION_UNAVAILABLE = 2; // The acquisition of the geolocation failed because at least one internal source of position returned an internal error.
const GEOLOCATION_ERROR_TIMEOUT = 3; // The time allowed to acquire the geolocation, defined by PositionOptions.timeout information was reached before the information was obtained.

const Geolocation = function () {
    const supported_geolocation = global.app.supported("geolocation");
    var watchers = new WeakIterable();
    var watch = null,
        position = null;
    var available = false,
        required = false;
    var geolocation_permission_state = null;

    const OPTIONS_DEFAULT = {
        maximumAge: GEOLOCATION_MAXIAGE,
        timeout: GEOLOCATION_TIMEOUT
    };

    const HIGH_ACCURACY = Object.assign({}, OPTIONS_DEFAULT, {enableHighAccuracy: true});
    const LOW_ACCURACY = Object.assign({}, OPTIONS_DEFAULT, {enableHighAccuracy: false});

    Object.defineProperty(this, "available", {
        set: () => {},
        get: () => available
    });

    Object.defineProperty(this, "required", {
        set: () => {},
        get: () => required
    });

    const geolocation_permission_state_change = (state) => {
        log.info("geolocation permission state changed", geolocation_permission_state, "->", state);
        geolocation_permission_state = state;
        switch (state) {
            case "denied":
                this.disable();
                available = false;
                log.warn("geolocation permission denied", {available, required});
                break;
            case "prompt":
                this.disable();
                available = false;
                log.warn("waiting for user approval of geolocation permission", {available, required});
                break;
            case "granted":
                available = true;
                if (required) {
                    this.enable();
                }
                log.warn("geolocation permission granted", {available, required});
                break;
        }
        if (state === "denied" || state === "prompt") {
            AlertArea.interact({
                type: "warning",
                tip1: global.internationalization.text("statusbar.Activate-device-location")
            });
        }
        if (state === "granted") {
            // hide the modal in case it is displayed when the user gives permission
            AlertArea.hide();
        }
    };

    this.init = async () => {
        log.debug("Geolocation.init IN");
        if (global.configuration.user.agnet || !supported_geolocation) {
            return;
        }
        navigator.geolocation.getCurrentPosition(() => {}); // trigger permission query
        const new_geolocation_permission = await navigator.permissions.query({name: "geolocation"});
        geolocation_permission_state_change(new_geolocation_permission.state);
        new_geolocation_permission.onchange = () => {
            // show or hide warning modal when the user changes the permission status
            geolocation_permission_state_change(new_geolocation_permission.state);
        };
    };

    const position_change = (current_position) => {
        log.debug("Geolocation.position_change IN");
        position = current_position;
        for (let handler of watchers) {
            handler(position);
        }
    };

    const position_error = (error) => {
        switch (error.code) {
            case GEOLOCATION_ERROR_PERMISSION_DENIED:
                log.info("position retrieval error: permission denied");
                break;
            case GEOLOCATION_ERROR_POSITION_UNAVAILABLE:
                log.info("position retrieval error: position unavailable");
                break;
            case GEOLOCATION_ERROR_TIMEOUT:
                log.info("position retrieval error: position timeout");
                break;
        }
        if (sm.state.name === "enabled") {
            sm.transition.errored({});
        }
    };

    const get_current_position = (options) => {
        log.debug("Geolocation.get_current_position IN");
        return new Promise((resolve, reject) => {
            if (!supported_geolocation) {
                reject();
            }
            navigator.geolocation.getCurrentPosition(
                (current_position) => {
                    position = current_position;
                    position_change(position);
                    resolve(current_position);
                },
                (error) => {
                    position_error(error);
                    reject(error);
                },
                options
            );
        });
    };

    this.current_position_high_accuracy = async () => {
        log.debug("Geolocation.current_position_high_accuracy IN");
        try {
            return await get_current_position(HIGH_ACCURACY);
        } catch (error) {
            log.error("error during high accuracy position fetching", error);
            return null;
        }
    };

    this.current_position_low_accuracy = async () => {
        log.debug("Geolocation.current_position_low_accuracy IN");
        try {
            return await get_current_position(LOW_ACCURACY);
        } catch (error) {
            log.error("error during low accuracy position fetching", error);
            return null;
        }
    };

    this.current_position = async () => {
        log.debug("Geolocation.current_position IN");
        return this.current_position_high_accuracy();
    };

    const enable_watch = async () => {
        log.debug("Geolocation.enable_watch IN");
        if (watch !== null) {
            return;
        }
        watch = navigator.geolocation.watchPosition(position_change, position_error, HIGH_ACCURACY);
    };

    const disable_watch = async () => {
        log.debug("Geolocation.disable_watch IN");
        if (watch === null) {
            return;
        }
        navigator.geolocation.clearWatch(watch);
        watch = null;
    };

    const update_required = () => {
        log.debug("Geolocation.update_required IN");
        if (watchers.length === 0) {
            this.disable();
            required = false;
            return;
        }
        required = true;
        if (available) {
            this.enable();
        }
    };

    this.on_position_change = async (handler) => {
        log.debug("Geolocation.on_position_change IN");
        watchers.add(handler);
        update_required();
    };

    this.off_position_change = async (handler) => {
        log.debug("Geolocation.off_position_change IN");
        watchers.delete(handler);
        update_required();
    };

    var sm = new StateMachine({
        name: "geolocation",
        shared_data: {},
        default_state: "disabled",
        states: {
            disabled: {
                in: () => {
                    global.event_bus.publish("geolocation.state", {state: "disabled"});
                },
                out: () => {},
                transit: {
                    enable: {
                        state: "enabled",
                        handler: async () => {
                            // checking that geolocation is available
                            if (!supported_geolocation) {
                                log.error("geolocation can't be enabled, it is not supported on your system");
                                return false;
                            }
                            return true;
                        }
                    }
                },
                schedule: {}
            },
            errored: {
                in: () => {
                    global.event_bus.publish("geolocation.state", {state: "errored"});
                },
                out: () => {},
                transit: {
                    reenable: {
                        state: "enabled",
                        handler: async () => {
                            try {
                                var new_position = await get_current_position(HIGH_ACCURACY);
                                if (Date.now() - new_position.timestamp > GEOLOCATION_MAXIAGE) {
                                    return false;
                                }
                                return true;
                            } catch (error) {
                                return false;
                            }
                        }
                    },
                    disable: {
                        state: "disabled",
                        handler: () => true
                    }
                },
                schedule: {
                    retry: {
                        handler: async () => {
                            log.debug("Geolocation.errored.schedule");
                            sm.transition.enabled({});
                        },
                        interval: 5 * 1e3, // 5 * 1s = 5 seconds
                        repeat: Infinity
                    }
                }
            },
            enabled: {
                in: () => {
                    log.debug("Geolocation.sm.enabled");
                    enable_watch();
                    global.event_bus.publish("geolocation.state", {state: "enabled"});
                },
                out: () => {
                    disable_watch();
                },
                transit: {
                    error: {
                        state: "errored",
                        handler: () => true
                    },
                    disable: {
                        state: "disabled",
                        handler: () => true
                    }
                },
                schedule: {
                    check: {
                        handler: async () => {
                            log.debug("Geolocation.enabled.schedule");
                            // last position is older than 15s
                            if (position === null || Date.now() - position.timestamp > GEOLOCATION_MAXIAGE) {
                                try {
                                    var new_position = await get_current_position(HIGH_ACCURACY);
                                    if (Date.now() - new_position.timestamp > GEOLOCATION_MAXIAGE) {
                                        sm.transition.errored();
                                    }
                                } catch (error) {
                                    sm.transition.errored();
                                }
                            }
                        },
                        interval: 15e3, // 15 * 1s = 15 seconds
                        repeat: Infinity
                    }
                }
            }
        }
    });

    Object.defineProperty(this, "state", {
        get: () => sm.state.name,
        set: (state) => {
            log.debug("Geolocation.state = ", {state});
            if (state in sm.transition) {
                sm.transition[state]();
            } else {
                log.error(`transition to state ${state} impossible`);
            }
        }
    });

    this.enable = () => {
        log.debug("Geolocation.enable IN");
        sm.transition.enabled({});
    };

    this.disable = () => {
        log.debug("Geolocation.disable IN");
        sm.transition.disabled({});
    };

    this.simulate_position = (position) => {
        position_change(position);
    };

    this.debug = new GeolocationDebug(this);
};

global.app.bootstrap.register_module({
    name: "geolocation",
    deps: ["app"],
    init: async () => {
        global.geolocation = new Geolocation();
        await global.geolocation.init();
    }
});

export default Geolocation;
